import React, { useState } from "react";
import { useT } from "../i18n/LanguageContext";

const buildMenuItems = (t) => [
  { key: "dashboard", label: t("adminNav.dashboard") },
  { heading: t("adminNav.masterData") },
  { key: "academic-sessions", label: t("adminNav.academicSessions"), permission: "master:academic-sessions" },
  { key: "degree-types", label: t("adminNav.degreeTypes"), permission: "master:degree-types" },
  { key: "faculties", label: t("adminNav.faculties"), permission: "master:faculties" },
  { key: "subjects", label: t("adminNav.subjects"), permission: "master:subjects" },
  { key: "subject-categories", label: t("adminNav.subjectCategories"), permission: "master:subject-categories" },
  { key: "subject-basket", label: t("adminNav.subjectBasket"), permission: "master:subject-baskets" },
  { key: "degrees", label: t("adminNav.degrees"), permission: "master:degrees" },
  { key: "fee-heads", label: t("adminNav.feeHeads"), permission: "master:fee-heads" },
  { key: "fee-rules", label: t("adminNav.feeRules"), permission: "master:fee-rules" },
  { key: "document-types", label: t("adminNav.documentTypes"), permission: "master:document-types" },
  { key: "document-requirements", label: t("adminNav.documentRequirements"), permission: "master:document-requirements" },
  { key: "qualification-configs", label: t("adminNav.qualificationConfigs"), permission: "master:qualification-configs" },
  { key: "course-seats", label: t("adminNav.courseSeats"), permission: "master:course-seats" },
  { key: "admission-processes", label: t("adminNav.admissionProcesses"), permission: "master:admission-processes" },
  { key: "form-field-config", label: t("adminNav.formFieldConfig"), permission: "master:form-field-config" },
  { key: "translations", label: "Translations", permission: "master:translations" },
  { heading: t("adminNav.admissions") },
  { key: "merit-list", label: t("adminNav.meritList"), permission: "admissions:merit-list" },
  { key: "admission-list", label: t("adminNav.admissionList"), permission: "admissions:admission-list" },
  { heading: t("adminNav.system") },
  { key: "roles", label: t("adminNav.rolesPermissions"), permission: "system:roles" },
  { key: "admin-users", label: t("adminNav.adminUsers"), permission: "system:admins" },
];

function getVisibleSections(allMenuItems, permissions, isSystem) {
  const hasPerm = (code) => isSystem || permissions.includes(code);

  const topLevel = []; // items before any heading (e.g. Dashboard)
  const sections = []; // { heading, items }
  let current = null;

  for (const item of allMenuItems) {
    if (item.heading) {
      current = { heading: item.heading, items: [] };
      sections.push(current);
    } else if (!item.permission || hasPerm(item.permission)) {
      if (current) {
        current.items.push(item);
      } else {
        topLevel.push(item);
      }
    }
  }

  // Filter out sections with no visible items
  return { topLevel, sections: sections.filter((s) => s.items.length > 0) };
}

const SectionGroup = ({ heading, items, selected, onSelect, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-xs text-gray-400 uppercase tracking-wide hover:text-gray-200 transition-colors"
      >
        <span className="font-semibold">{heading}</span>
        <svg
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {items.map((item) => (
            <button
              key={item.key}
              className={`w-full text-left pl-5 pr-2 py-1.5 rounded text-sm ${
                selected === item.key
                  ? "bg-gray-700 text-white"
                  : "text-gray-300 hover:bg-gray-700 hover:text-white"
              }`}
              onClick={() => onSelect(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminSidebar = ({ onSelect, handleLogout, admin, selected }) => {
  const { t } = useT();
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const isSystem = admin.role?.isSystem || false;
  const allMenuItems = buildMenuItems(t);
  const { topLevel, sections } = getVisibleSections(allMenuItems, permissions, isSystem);

  // A section is open by default if it contains the currently selected item
  const sectionHasSelected = (items) => items.some((item) => item.key === selected);

  return (
    <div className="h-full overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
      <div className="w-full md:w-64 bg-gray-900 text-white h-full p-4 flex flex-col">
        <h2 className="text-xl font-semibold mb-4 border-b border-gray-700 pb-2">
          {t("adminNav.adminPanel")}
        </h2>
        <p className="text-sm text-gray-300 mb-1">
          {t("adminNav.welcome")}, <span className="font-semibold">{admin.name}</span>
        </p>
        <p className="text-xs text-gray-500 mb-6 capitalize">
          {admin.role?.name || "admin"}
        </p>

        <nav className="flex-1">
          {/* Top-level items (Dashboard) */}
          <div className="space-y-0.5">
            {topLevel.map((item) => (
              <button
                key={item.key}
                className={`w-full text-left px-2 py-1.5 rounded text-sm ${
                  selected === item.key
                    ? "bg-gray-700 text-white"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white"
                }`}
                onClick={() => onSelect(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Collapsible sections */}
          {sections.map((section) => (
            <SectionGroup
              key={section.heading}
              heading={section.heading}
              items={section.items}
              selected={selected}
              onSelect={onSelect}
              defaultOpen={sectionHasSelected(section.items)}
            />
          ))}
        </nav>

        <div className="pt-3 mt-3 border-t border-gray-700">
          <button
            className="w-full text-left text-red-400 px-2 py-2 hover:bg-red-700 hover:text-white rounded text-sm"
            onClick={handleLogout}
          >
            {t("common.logout")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
